import { MusicalNote } from "../MusicalNote";
import { TimingInfo } from "./MasterComposer";
import {
  FillPattern,
  HiHatPattern,
  KickSnarePattern,
} from "./PatternInterfaces";
import { PhraseManager } from "./PhraseManager";

/**
 * Drum types used in the drum kit synthesis.
 */
export type DrumType =
  | "kick"
  | "snare"
  | "hihat_closed"
  | "hihat_open"
  | "crash"
  | "tom_low"
  | "tom_mid"
  | "tom_high";

/**
 * Helper function to get MIDI note number for drum types.
 */
export function getDrumNoteNumber(drumType: DrumType): number {
  const drumMap: Record<DrumType, number> = {
    kick: 36,
    snare: 38,
    hihat_closed: 42,
    hihat_open: 46,
    crash: 49,
    tom_low: 41,
    tom_mid: 43,
    tom_high: 45,
  };
  return drumMap[drumType];
}

// ===== HI-HAT PATTERNS =====

/**
 * Simple quarter note hi-hat pattern.
 */
export class HiHatQuartersPattern implements HiHatPattern {
  execute(timingInfo: TimingInfo, intensity: number): MusicalNote[] {
    const beat = timingInfo.beatInPhrase % 4;
    const notes: MusicalNote[] = [];

    switch (beat) {
      case 0:
      case 1:
      case 2:
      case 3:
        notes.push({
          startBeat: 0,
          durationBeats: 0.1,
          noteNumber: getDrumNoteNumber("hihat_closed"),
          velocity: 0.7 + intensity * 0.2,
        });
        break;
    }

    return notes;
  }
}

/**
 * Eighth note hi-hat pattern.
 */
export class HiHatEighthsPattern implements HiHatPattern {
  execute(timingInfo: TimingInfo, intensity: number): MusicalNote[] {
    const beat = timingInfo.beatInPhrase % 4;
    const notes: MusicalNote[] = [];

    switch (beat) {
      case 0:
      case 1:
      case 2:
      case 3:
        // On-beat (stronger)
        notes.push({
          startBeat: 0,
          durationBeats: 0.1,
          noteNumber: getDrumNoteNumber("hihat_closed"),
          velocity: 0.8 + intensity * 0.2,
        });
        // Off-beat (weaker)
        notes.push({
          startBeat: 0.5,
          durationBeats: 0.1,
          noteNumber: getDrumNoteNumber("hihat_closed"),
          velocity: 0.5 + intensity * 0.2,
        });
        break;
    }

    return notes;
  }
}

// ===== KICK & SNARE PATTERNS =====

/**
 * Basic rock pattern: kick on 1 & 3, snare on 2 & 4.
 */
export class KickSnareRockPattern implements KickSnarePattern {
  execute(timingInfo: TimingInfo, intensity: number): MusicalNote[] {
    const beat = timingInfo.beatInPhrase % 4;
    const notes: MusicalNote[] = [];

    switch (beat) {
      case 0: // Beat 1
        notes.push({
          startBeat: 0,
          durationBeats: 0.1,
          noteNumber: getDrumNoteNumber("kick"),
          velocity: 0.9,
        });
        break;
      case 1: // Beat 2
        notes.push({
          startBeat: 0,
          durationBeats: 0.1,
          noteNumber: getDrumNoteNumber("snare"),
          velocity: 0.8,
        });
        // Add ghost snare at higher intensity
        if (intensity > 0.7) {
          notes.push({
            startBeat: 0.75,
            durationBeats: 0.1,
            noteNumber: getDrumNoteNumber("snare"),
            velocity: 0.3,
          });
        }
        break;
      case 2: // Beat 3
        notes.push({
          startBeat: 0,
          durationBeats: 0.1,
          noteNumber: getDrumNoteNumber("kick"),
          velocity: 0.9,
        });
        // Add extra kick at higher intensity
        if (intensity > 0.5) {
          notes.push({
            startBeat: 0.5,
            durationBeats: 0.1,
            noteNumber: getDrumNoteNumber("kick"),
            velocity: 0.7,
          });
        }
        break;
      case 3: // Beat 4
        notes.push({
          startBeat: 0,
          durationBeats: 0.1,
          noteNumber: getDrumNoteNumber("snare"),
          velocity: 0.8,
        });
        break;
    }

    // Apply intensity-based velocity scaling
    return notes.map((note) => ({
      ...note,
      velocity: (note.velocity || 0.7) * (0.7 + intensity * 0.3),
    }));
  }
}

/**
 * Funkier pattern with more syncopated kick.
 */
export class KickSnareFunkPattern implements KickSnarePattern {
  execute(timingInfo: TimingInfo, intensity: number): MusicalNote[] {
    const beat = timingInfo.beatInPhrase % 4;
    const notes: MusicalNote[] = [];

    switch (beat) {
      case 0: // Beat 1
        notes.push({
          startBeat: 0,
          durationBeats: 0.1,
          noteNumber: getDrumNoteNumber("kick"),
          velocity: 0.9,
        });
        if (intensity > 0.6) {
          notes.push({
            startBeat: 0.25,
            durationBeats: 0.1,
            noteNumber: getDrumNoteNumber("kick"),
            velocity: 0.6,
          });
        }
        break;
      case 1: // Beat 2
        notes.push({
          startBeat: 0,
          durationBeats: 0.1,
          noteNumber: getDrumNoteNumber("snare"),
          velocity: 0.8,
        });
        break;
      case 2: // Beat 3
        if (intensity > 0.4) {
          notes.push({
            startBeat: 0.5,
            durationBeats: 0.1,
            noteNumber: getDrumNoteNumber("kick"),
            velocity: 0.7,
          });
        }
        break;
      case 3: // Beat 4
        notes.push({
          startBeat: 0,
          durationBeats: 0.1,
          noteNumber: getDrumNoteNumber("snare"),
          velocity: 0.8,
        });
        if (intensity > 0.7) {
          notes.push({
            startBeat: 0.75,
            durationBeats: 0.1,
            noteNumber: getDrumNoteNumber("kick"),
            velocity: 0.6,
          });
        }
        break;
    }

    return notes.map((note) => ({
      ...note,
      velocity: (note.velocity || 0.7) * (0.7 + intensity * 0.3),
    }));
  }
}

// ===== FILL PATTERNS =====

/**
 * Simple small fill (2 beats) - snare roll.
 */
export class SmallSnareFillPattern implements FillPattern {
  getFillType(): "small" | "medium" | "large" {
    return "small";
  }

  execute(timingInfo: TimingInfo, intensity: number): MusicalNote[] {
    const beatInFill = PhraseManager.getBeatInFill(timingInfo);
    const beat = beatInFill % 2; // 2-beat pattern
    const notes: MusicalNote[] = [];

    switch (beat) {
      case 0:
        // Snare on beat
        notes.push({
          startBeat: 0,
          durationBeats: 0.1,
          noteNumber: getDrumNoteNumber("snare"),
          velocity: 0.7,
        });
        // Add sixteenth notes at higher intensity
        if (intensity > 0.5) {
          notes.push({
            startBeat: 0.25,
            durationBeats: 0.1,
            noteNumber: getDrumNoteNumber("snare"),
            velocity: 0.5,
          });
          notes.push({
            startBeat: 0.5,
            durationBeats: 0.1,
            noteNumber: getDrumNoteNumber("snare"),
            velocity: 0.6,
          });
          notes.push({
            startBeat: 0.75,
            durationBeats: 0.1,
            noteNumber: getDrumNoteNumber("snare"),
            velocity: 0.5,
          });
        }
        break;
      case 1:
        // Build up to next section
        notes.push({
          startBeat: 0,
          durationBeats: 0.1,
          noteNumber: getDrumNoteNumber("snare"),
          velocity: 0.8,
        });
        notes.push({
          startBeat: 0.5,
          durationBeats: 0.1,
          noteNumber: getDrumNoteNumber("snare"),
          velocity: 0.9,
        });
        break;
    }

    return notes;
  }
}

/**
 * Medium fill (4 beats) - tom and snare combination.
 */
export class MediumTomFillPattern implements FillPattern {
  getFillType(): "small" | "medium" | "large" {
    return "medium";
  }

  execute(timingInfo: TimingInfo, intensity: number): MusicalNote[] {
    const beatInFill = PhraseManager.getBeatInFill(timingInfo);
    const beat = beatInFill % 4; // 4-beat pattern
    const notes: MusicalNote[] = [];

    switch (beat) {
      case 0:
        notes.push({
          startBeat: 0,
          durationBeats: 0.1,
          noteNumber: getDrumNoteNumber("tom_high"),
          velocity: 0.7,
        });
        break;
      case 1:
        notes.push({
          startBeat: 0,
          durationBeats: 0.1,
          noteNumber: getDrumNoteNumber("tom_mid"),
          velocity: 0.7,
        });
        notes.push({
          startBeat: 0.5,
          durationBeats: 0.1,
          noteNumber: getDrumNoteNumber("tom_mid"),
          velocity: 0.6,
        });
        break;
      case 2:
        notes.push({
          startBeat: 0,
          durationBeats: 0.1,
          noteNumber: getDrumNoteNumber("tom_low"),
          velocity: 0.8,
        });
        break;
      case 3:
        // Build up with snare
        notes.push({
          startBeat: 0,
          durationBeats: 0.1,
          noteNumber: getDrumNoteNumber("snare"),
          velocity: 0.7,
        });
        notes.push({
          startBeat: 0.5,
          durationBeats: 0.1,
          noteNumber: getDrumNoteNumber("snare"),
          velocity: 0.8,
        });
        // Crash on last beat to transition
        notes.push({
          startBeat: 0.75,
          durationBeats: 0.1,
          noteNumber: getDrumNoteNumber("crash"),
          velocity: 0.9,
        });
        break;
    }

    return notes;
  }
}

/**
 * Large fill (8 beats) - complex tom and crash combination.
 */
export class LargeCrashFillPattern implements FillPattern {
  getFillType(): "small" | "medium" | "large" {
    return "large";
  }

  execute(timingInfo: TimingInfo, intensity: number): MusicalNote[] {
    const beatInFill = PhraseManager.getBeatInFill(timingInfo);
    const beat = beatInFill % 8; // 8-beat pattern
    const notes: MusicalNote[] = [];

    switch (beat) {
      case 0:
      case 2:
      case 4:
        notes.push({
          startBeat: 0,
          durationBeats: 0.1,
          noteNumber: getDrumNoteNumber("tom_high"),
          velocity: 0.6,
        });
        break;
      case 1:
      case 3:
      case 5:
        notes.push({
          startBeat: 0,
          durationBeats: 0.1,
          noteNumber: getDrumNoteNumber("tom_mid"),
          velocity: 0.7,
        });
        break;
      case 6:
        notes.push({
          startBeat: 0,
          durationBeats: 0.1,
          noteNumber: getDrumNoteNumber("tom_low"),
          velocity: 0.8,
        });
        notes.push({
          startBeat: 0.5,
          durationBeats: 0.1,
          noteNumber: getDrumNoteNumber("snare"),
          velocity: 0.9,
        });
        break;
      case 7:
        // Big finish
        notes.push({
          startBeat: 0,
          durationBeats: 0.1,
          noteNumber: getDrumNoteNumber("crash"),
          velocity: 1.0,
        });
        notes.push({
          startBeat: 0,
          durationBeats: 0.1,
          noteNumber: getDrumNoteNumber("kick"),
          velocity: 1.0,
        });
        break;
    }

    return notes;
  }
}
